import { useState } from "react";
import { useSelector } from "react-redux";
import {
  useFloating,
  offset,
  useClick,
  useDismiss,
  useRole,
  useInteractions,
  flip,
  shift,
} from "@floating-ui/react";
import DefaultButton from "../buttons/defaultButton";
import { HiOutlineX } from "react-icons/hi";
import { FiFilter } from "react-icons/fi";
import { selectModuleFilter } from "../../../store/slices/filterSorting.slice";

const AdvanceFilter = ({
  isFiltering = false,
  onFilterChange = () => {},
  onClearFilters = () => {},
  isFilterDisable = false,
  closeFilter = () => {},
  children,
  filterModule = "default",
  boxWidth = "w-72",
}) => {
  const [open, setOpen] = useState(false);

  // Get active filter state from Redux based on module
  const filterState = useSelector((state) =>
    selectModuleFilter(state, filterModule)
  );
  const hasActiveFilters = filterState?.isFilter || false;

  const { refs, floatingStyles, context } = useFloating({
    open,
    onOpenChange: setOpen,
    middleware: [offset(8), flip(), shift({ padding: 8 })],
    placement: "bottom-start",
    strategy: "fixed",
  });

  const { getReferenceProps, getFloatingProps } = useInteractions([
    useClick(context),
    useDismiss(context),
    useRole(context, { role: "menu" }),
  ]);

  const handleApply = () => {
    onFilterChange();
    setOpen(false);
  };

  const handleClear = () => {
    onClearFilters();
    // Don't call onFilterChange after clear - let onClearFilters handle the refetch
    setOpen(false);
  };

  return (
    <div className="relative inline-block">
      <DefaultButton
        ref={refs.setReference}
        {...getReferenceProps()}
        classnames={
          hasActiveFilters ? "bg-gradient-to-r from-[#1E3A8A] to-[#3B82F6] text-white gap-1" : "bg-white gap-1"
        }
        icon={<FiFilter className="w-4 h-4" />}
        tooltip={hasActiveFilters ? "Filtered" : "Filter"}
        disabled={isFilterDisable}
      />

      {open && (
        <div
          ref={refs.setFloating}
          style={floatingStyles}
          {...getFloatingProps()}
          className={`z-[9999] rounded-2xl border border-gray-200 bg-white p-5 shadow-xl space-y-4 ${boxWidth}`}
        >
          <div className="flex justify-between">
            <p className="text-md font-semibold text-primary-950">Filter</p>
            <div className="flex items-center gap-2">
              <button
                onClick={handleClear}
                className="cursor-pointer flex items-center gap-1 text-primary-600 hover:text-primary-950 hover:underline text-sm"
                disabled={!hasActiveFilters}
              >
                Clear
              </button>
              <button
                onClick={() => setOpen(false)}
                className="ml-2 p-1 rounded-full hover:bg-gray-200 text-gray-500 hover:text-gray-900"
                aria-label="Close"
              >
                <HiOutlineX className="w-5 h-5" />
              </button>
            </div>
          </div>
          {/* Filter Content */}
          <div className="space-y-4">{children}</div>
          {/* Footer Buttons */}
          <div className="flex justify-end space-x-2 pt-2 border-t border-gray-100 rounded-b-2xl bg-white">
            <DefaultButton
              variant="outline"
              name={"Cancel"}
              onClick={() => setOpen(false)}
            />
            <DefaultButton name={"Apply"} onClick={handleApply} />
          </div>
        </div>
      )}
    </div>
  );
};

export default AdvanceFilter;
